import { Star } from '@mui/icons-material'
import { Grid } from '@mui/material'
import { uniq } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect } from 'react'
import { getI18n, useTranslation } from 'react-i18next'

import { IPersonProfileSkill } from '../../../../interfaces/skills/ISkills'
import { useStore } from '../../../../Provider'
import { OPLoadingCircle } from '../../../OPcomponents/OPLoadingcircle'
import { Data, OPNewTable } from '../../../OPcomponents/OPNewTable'
import { OPSkillbar } from '../../../OPcomponents/OPSkillbar'

import { getLocalizedRoleName } from '../../../../common/roleHelper'
import { IProfileProjectInfo, IProjectPersonDetails } from '../../../../interfaces/projects/IProjects'
import ProfileSkillForm from './ProfileSkillForm'

interface userId {
  userId: string
}

const ProfileSkills = ({ userId }: userId): JSX.Element => {
  const { t } = useTranslation()
  const rootStore = useStore()
  const {
    getPersonPrivateProjects,
    getPersonPublicProjects,
    privateWorkHistory,
    privateWorkHistoryLoading,
    publicWorkHistory,
    publicWorkHistoryLoading,
    getPersonSkills,
    loadingSkills,
    filteredSkills,
  } = rootStore.profileStore
  const { allSkills, getAllSkills } = rootStore.skillStore
  const { validatedUser } = rootStore.loginStore
  const { getAllProjects } = rootStore.projectStore

  useEffect(() => {
    async function fetchData() {
      await Promise.all([
        getPersonSkills(Number(userId)),
        getAllSkills(Number(userId)),
        getAllProjects(Number(userId)),
        getPersonPrivateProjects(Number(userId)),
        getPersonPublicProjects(Number(userId)),
      ])
    }
    fetchData()
  }, [getAllProjects, getAllSkills, getPersonPrivateProjects, getPersonPublicProjects, getPersonSkills, userId, validatedUser])

  const rows: Data[] = filteredSkills.map((skill) => ({
    id: skill.skillId,
    rows: {
      [t('Skills.SkillName')]: {
        text: getI18n().language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi,
        width: '30%',
      },
      [t('Skills.Level')]: {
        text: (
          <OPSkillbar
            name={getI18n().language === 'en' && skill.skillNameEng?.length ? skill.skillNameEng : skill.skillNameFi}
            value={skill.skillLevel}
            readonly
          />
        ),
        width: '20%',
      },
      [t('Skills.KeySkill')]: {
        text: skill.isKeySkill ? <Star color='primary' /> : '',
        width: '9%',
        align: 'center' as const,
      },
      [t('Skills.Expirience')]: { text: `${skill.experienceDays} ${t('Date.ExperienceDays')} `, width: '10%' },
      [t('Skills.Projects')]: { text: skill.linkedProjectNames?.join(', '), width: '30%' },
    },
  }))

  const onRowClick = async (id: number | string) => {
    const curClicked = allSkills.find((skill) => skill.skill.skillId === id)?.skill
    const userInfo = filteredSkills.find((skill) => skill.skillId === id)
    if (curClicked) {
      const uniqueProjects = uniq<string>(userInfo?.linkedProjectNames ?? [])

      const detailedProjectInfo = await Promise.all(
        uniqueProjects.map(async (project) => {
          const pubWorkHistory = publicWorkHistory?.filter(
            (p) => p.projectName === project && p.linkedSkills.findIndex((s) => s.skillId === id) !== -1,
          )
          const privWorkHistory = privateWorkHistory?.filter(
            (p) => p.projectName === project && p.linkedSkills.findIndex((s) => s.skillId === id) !== -1,
          ) as unknown as IProjectPersonDetails[]

          const projects = [...pubWorkHistory, ...privWorkHistory]

          const projectsInfo: IProfileProjectInfo[] = await Promise.all(
            projects?.map(async (p) => {
              const projectId = p.projectId
              const projectinfo = await rootStore.projectStore.getDetailedUserProjects(Number(userId), Number(projectId))
              return {
                projectId,
                projectName: projectinfo?.projectName,
                startDate: projectinfo?.startDate?.toString(),
                endDate: projectinfo?.endDate?.toString(),
                descriptionFi: projectinfo?.descriptionFi,
                descriptionEng: projectinfo?.descriptionEng,
                workAmount: projectinfo?.expDays,
                roles: projectinfo?.roles.map((role) => getLocalizedRoleName(role)).join(', '),
              }
            }),
          )

          return projectsInfo
        }),
      )

      const skillInfo: IPersonProfileSkill = {
        skill: {
          skillNameFi: curClicked.skillNameFi,
          skillNameEng: curClicked.skillNameEng,
          skillOverview: curClicked.skillDescription,
          skillCategory: curClicked.categoryName,
          skillTags: curClicked.skillTags,
        },
        personSkill: {
          skillLevel: userInfo.skillLevel,
          skillComment: userInfo.comment,
          linkedProjects: detailedProjectInfo.flat(),
          isKeySkill: userInfo.isKeySkill,
        },
      }
      return skillInfo
    }
  }

  return (
    <Grid container spacing={5} className='gridcontainer--formcontainer__addnewpaddings'>
      {loadingSkills || privateWorkHistoryLoading || publicWorkHistoryLoading ? (
        <Grid item xs={12}>
          <OPLoadingCircle />
        </Grid>
      ) : (
        <Grid item xs={12}>
          {filteredSkills.length > 0 ? (
            <OPNewTable rows={rows} curLength={rows.length} onRowClick={onRowClick} FormBase={ProfileSkillForm} />
          ) : (
            <p className='profile--deptext profpadding'>{t('Profile.NoAddedSkills')}</p>
          )}
        </Grid>
      )}
    </Grid>
  )
}

export default observer(ProfileSkills)
